import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, Clock, 
  MessageSquare, ShoppingCart, ClipboardList, FileText,
  ArrowLeft, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDate } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const NotificationHistory = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, message, order, form, system

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user-notifications/history`, getAuthHeader());
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // Fallback to regular notifications endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/user-notifications`, getAuthHeader());
        setNotifications(fallbackResponse.data || []);
      } catch (fallbackError) {
        toast.error('Failed to load notification history');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/user-notifications/${notificationId}/read`, {}, getAuthHeader());
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      // Dispatch event to update badge in Layout
      window.dispatchEvent(new CustomEvent('notificationRead', { detail: { notificationId } }));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/user-notifications/mark-all-read`, {}, getAuthHeader());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      // Dispatch event to update badge in Layout
      window.dispatchEvent(new CustomEvent('allNotificationsRead'));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/user-notifications/${notificationId}`, getAuthHeader());
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'order':
      case 'order_request':
        return <ShoppingCart className="w-5 h-5 text-purple-500" />;
      case 'order_notification':
        return <ClipboardList className="w-5 h-5 text-amber-500" />;
      case 'form':
        return <FileText className="w-5 h-5 text-emerald-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'message': return 'Message';
      case 'order': 
      case 'order_request': return 'Order Request';
      case 'order_notification': return 'Order Update';
      case 'form': return 'Form';
      case 'pen_assignment': return 'Pen Assignment';
      default: return 'System';
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    // Read/Unread filter
    if (filter === 'unread' && n.is_read) return false;
    if (filter === 'read' && !n.is_read) return false;
    
    // Type filter
    if (typeFilter !== 'all') {
      const notifType = n.type || 'system';
      if (typeFilter === 'message' && notifType !== 'message') return false;
      if (typeFilter === 'order' && !['order', 'order_request', 'order_notification'].includes(notifType)) return false;
      if (typeFilter === 'form' && notifType !== 'form') return false;
      if (typeFilter === 'system' && !['pen_assignment', 'system'].includes(notifType)) return false;
    }
    
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6" data-testid="notification-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notification History
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            View all your past notifications
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchNotifications}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <CheckCheck className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600">Filter by:</span>
        </div>
        
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ({notifications.length})</SelectItem>
            <SelectItem value="unread">Unread ({unreadCount})</SelectItem>
            <SelectItem value="read">Read ({notifications.length - unreadCount})</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="message">Messages</SelectItem>
            <SelectItem value="order">Orders</SelectItem>
            <SelectItem value="form">Forms</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>

        {(filter !== 'all' || typeFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setFilter('all'); setTypeFilter('all'); }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Notification List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading notifications...</div>
      ) : filteredNotifications.length === 0 ? (
        <div className="card p-12 text-center">
          <BellOff className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg text-slate-500 mb-2">No notifications found</p>
          <p className="text-sm text-slate-400">
            {filter !== 'all' || typeFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'You have no notifications yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <div 
              key={notification.id}
              data-testid={`notification-${notification.id}`}
              className={`card p-4 transition-all ${
                !notification.is_read 
                  ? 'border-l-4 border-l-emerald-500 bg-emerald-50/50' 
                  : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-full ${
                  !notification.is_read ? 'bg-emerald-100' : 'bg-slate-100'
                }`}>
                  {getNotificationIcon(notification.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-semibold ${
                      !notification.is_read ? 'text-slate-900' : 'text-slate-700'
                    }`}>
                      {notification.title}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      !notification.is_read 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {getNotificationTypeLabel(notification.type)}
                    </span>
                    {!notification.is_read && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        NEW
                      </span>
                    )}
                  </div>
                  
                  <p className="text-slate-600 text-sm mb-2">
                    {notification.message || notification.content}
                  </p>
                  
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(notification.created_at)}
                    </span>
                    {notification.is_read && notification.read_at && (
                      <span className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Read {formatDate(notification.read_at)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!notification.is_read && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => markAsRead(notification.id)}
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => deleteNotification(notification.id)}
                    title="Delete notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {notifications.length > 0 && (
        <div className="text-center text-sm text-slate-500 pt-4 border-t">
          Showing {filteredNotifications.length} of {notifications.length} notifications
          {unreadCount > 0 && ` • ${unreadCount} unread`}
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;
